// Parity P1 wave 1: the work pane drawn from the board's task layout (hybrid design §3.1
// "Sections from the board layout (§3.3), per-person collapse in `me/prefs`"). Each section is a
// heading with a chevron (folded per person) over its columns, side by side when the pane is wide
// enough (a container query: the same layout reads in the drawer, the full page and the Inbox
// preview), stacked otherwise. Items are registered blocks, a field (`field:<id>`), the board's
// remaining fields (`fields:rest`) or one titled group of those.
import { cn } from "@companyos/ui";
import { ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import type { ReactNode } from "react";
import { FieldLine } from "../task-page/fields";
import type { FieldDef } from "../view-engine";
import { useTaskHost } from "./host-context";
import { itemBlocks, type LayoutItem, restFields, type TaskLayout } from "./layout";
import { type TaskBlockProps, taskBlock } from "./registry";

/** Static classes (Tailwind sees them): a column's span of 12 once the pane is ≥ 560 px. */
const SPAN: Record<number, string> = {
  2: "@[560px]:col-span-2",
  3: "@[560px]:col-span-3",
  4: "@[560px]:col-span-4",
  5: "@[560px]:col-span-5",
  6: "@[560px]:col-span-6",
  7: "@[560px]:col-span-7",
  8: "@[560px]:col-span-8",
  9: "@[560px]:col-span-9",
  10: "@[560px]:col-span-10",
  11: "@[560px]:col-span-11",
  12: "@[560px]:col-span-12",
};

export type LayoutBodyProps = Omit<TaskBlockProps, "can"> & {
  can: TaskBlockProps["can"];
  layout: TaskLayout;
  /** Section ids this person folded. */
  collapsed: ReadonlySet<string>;
  onToggle: (sectionId: string) => void;
  /** Editor preview: draws every block's frame, never a block's own reads. */
  preview?: boolean;
};

/** The field ids the layout places in the body (`field:<id>`, and `fields:rest`'s). */
export function bodyFieldIds(layout: TaskLayout, fields: FieldDef[]): Set<string> {
  const ids = fields.map((f) => f.id);
  const placed = new Set<string>();
  for (const s of layout.sections) {
    if (s.hidden) continue;
    for (const c of s.columns)
      for (const it of c.items)
        for (const b of itemBlocks(it)) {
          if (b.startsWith("field:")) placed.add(b.slice(6));
          if (b === "fields:rest") for (const r of restFields(layout, ids)) placed.add(r);
        }
  }
  return placed;
}

export function LayoutBody(props: LayoutBodyProps) {
  const { layout, collapsed, onToggle } = props;
  return (
    <div data-slot="task-layout" className="@container mt-4 flex flex-col">
      {layout.sections
        .filter((s) => !s.hidden)
        .map((s) => {
          const folded = collapsed.has(s.id);
          const title = s.title ?? "";
          return (
            <section
              key={s.id}
              aria-label={title || "Section"}
              data-section={s.id}
              data-collapsed={folded || undefined}
              className="border-t border-subtle py-3 first:border-t-0"
            >
              {title ? (
                <button
                  type="button"
                  aria-expanded={!folded}
                  className="-ml-1 mb-1 flex items-center gap-1 rounded-sm px-1 text-xs font-semibold tracking-wide text-muted-foreground uppercase hover:text-foreground"
                  onClick={() => onToggle(s.id)}
                >
                  {folded ? (
                    <ChevronRightIcon aria-hidden="true" className="size-3.5" />
                  ) : (
                    <ChevronDownIcon aria-hidden="true" className="size-3.5" />
                  )}
                  {title}
                </button>
              ) : null}
              {folded ? null : (
                <div className="grid grid-cols-1 gap-x-6 @[560px]:grid-cols-12">
                  {s.columns.map((c, ci) => (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: columns have no id; order is identity.
                      key={ci}
                      data-slot="layout-column"
                      className={cn("flex min-w-0 flex-col", SPAN[c.width] ?? SPAN[12])}
                    >
                      {c.items.map((it, ii) => (
                        <Item key={typeof it === "string" ? it : `g${ii}`} item={it} {...props} />
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </section>
          );
        })}
    </div>
  );
}

function Item({ item, ...props }: { item: LayoutItem } & LayoutBodyProps) {
  if (typeof item === "string") return <Block id={item} {...props} />;
  return (
    <div
      data-slot="layout-group"
      className="mt-3 rounded-lg border border-subtle bg-sunken/60 px-3 py-2"
    >
      <p className="mb-1 text-xs font-medium text-muted-foreground">{item.group.title}</p>
      {item.group.blocks.map((b) => (
        <Block key={b} id={b} {...props} />
      ))}
    </div>
  );
}

function Block({ id, ...props }: { id: string } & LayoutBodyProps) {
  const { fields } = useTaskHost();
  const { api, company, task, can, layout } = props;
  if (id.startsWith("field:")) {
    const f = fields.find((x) => x.id === id.slice(6));
    if (!f) return null;
    return (
      <Frame>
        <FieldLine
          api={api}
          company={company}
          task={task}
          field={f}
          canEdit={can.edit}
          layout="block"
        />
      </Frame>
    );
  }
  if (id === "fields:rest") {
    const ids = new Set(
      restFields(
        layout,
        fields.map((f) => f.id),
      ),
    );
    const rest = fields.filter((f) => ids.has(f.id));
    if (!rest.length) return null;
    return (
      <Frame>
        <div className="flex flex-col gap-1">
          {rest.map((f) => (
            <FieldLine
              key={f.id}
              api={api}
              company={company}
              task={task}
              field={f}
              canEdit={can.edit}
            />
          ))}
        </div>
      </Frame>
    );
  }
  const block = taskBlock(id);
  if (!block) return null;
  const { Component } = block;
  return (
    <Frame block={id}>
      <Component
        api={api}
        company={company}
        task={task}
        board={props.board}
        host={props.host}
        can={can}
        onOpenTask={props.onOpenTask}
      />
    </Frame>
  );
}

function Frame({ block, children }: { block?: string; children: ReactNode }) {
  return (
    <div data-block={block} className="mt-3 min-w-0 first:mt-1">
      {children}
    </div>
  );
}
